// src/components/study/StudyPlanWizard.jsx
// Premium Consultation Flow - Black & White Theme
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import LogamiLogoSpinner from '../ui/LogamiLogoSpinner';
import { 
  ArrowRight, 
  CheckCircle, 
  Crown, 
  Lock,
  Target,
  TrendingUp,
  Crosshair,
  Globe,
  Clock,
  Calendar
} from 'lucide-react';
import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate';
import { useTranslation } from 'react-i18next';
import { auth } from '../../firebase';
import subscriptionService from '../../services/subscriptionService';
import studyPlanService from '../../services/studyPlanService';
import adminService from '../../services/adminService';

// Premium Toggle Card Component
const ToggleCard = ({ label, sublabel, isSelected, onClick, icon: Icon, disabled = false }) => (
  <motion.button
    onClick={onClick}
    disabled={disabled}
    whileHover={disabled ? {} : { scale: 1.02 }}
    whileTap={disabled ? {} : { scale: 0.98 }}
    className={`w-full text-left p-4 sm:p-6 rounded-2xl border-2 transition-all duration-300 flex items-center justify-between group min-h-[72px] ${
      isSelected
        ? 'bg-black border-black text-white shadow-xl'
        : 'bg-white border-gray-200 hover:border-black/30 text-black'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    <div className="flex items-center gap-3 sm:gap-4 flex-1">
      {Icon && (
        <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
          isSelected ? 'bg-white/20' : 'bg-black/5'
        }`}>
          <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${isSelected ? 'text-white' : 'text-black'}`} />
        </div>
      )}
      <div className="flex-1">
        <p className={`font-bold text-base sm:text-lg mb-1 ${isSelected ? 'text-white' : 'text-black'}`}>
          {label}
        </p>
        {sublabel && (
          <p className={`text-xs sm:text-sm ${isSelected ? 'text-white/80' : 'text-black/60'}`}>
            {sublabel}
          </p>
        )}
      </div>
    </div>
    {isSelected && (
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        className="flex-shrink-0"
      >
        <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
      </motion.div>
    )}
  </motion.button>
);

// Step Container with Animation
const StepContainer = ({ children, stepKey }) => (
  <motion.div
    key={stepKey}
    initial={{ opacity: 0, x: 50 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: -50 }}
    transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
    className="w-full"
  >
    {children}
  </motion.div>
);

// Step Header Component
const StepHeader = ({ icon: Icon, headline, subtext }) => (
  <div className="mb-6 sm:mb-8">
    <div className="flex items-center gap-3 sm:gap-4 mb-4">
      {Icon && (
        <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl bg-black flex items-center justify-center flex-shrink-0">
          <Icon className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
        </div>
      )}
      <div className="flex-1">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-black mb-2 tracking-tight">
          {headline}
        </h2>
        {subtext && (
          <p className="text-base sm:text-lg text-black/60">
            {subtext}
          </p>
        )}
      </div>
    </div>
  </div>
);

const StudyPlanWizard = ({ onPlanGenerated, isEmbedded = false, selectedExamData = null, isAdmin = false }) => {
  const { t } = useTranslation();
  const navigate = useLocalizedNavigate();
  const [step, setStep] = useState(1);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [isCheckingPremium, setIsCheckingPremium] = useState(false);
  
  // Map exam provider names to wizard format
  const mapProviderToWizard = (provider) => {
    if (!provider) return '';
    const mapping = {
      'goethe': 'Goethe',
      'osd': 'ÖSD',
      'telc': 'telc'
    };
    return mapping[provider.toLowerCase()] || provider;
  };
  
  // Infer current level from target level
  // If target is B1 → current is B1 (preparing for exam at that level)
  // If target is B2 → current is B1 (one level below)
  // If target is C1 → current is B2 (one level below)
  const inferCurrentLevel = (targetLevel) => {
    if (!targetLevel) return 'B1'; // Default fallback
    const levelMap = {
      'B1': 'B1',
      'B2': 'B1',
      'C1': 'B2'
    };
    return levelMap[targetLevel] || 'B1';
  };
  
  // Initialize formData with exam data from selectedExamData prop
  // Auto-infer currentLevel from targetLevel
  const targetLevel = selectedExamData?.level || '';
  const [formData, setFormData] = useState({
    currentLevel: inferCurrentLevel(targetLevel),
    targetLevel: targetLevel,
    targetExam: mapProviderToWizard(selectedExamData?.examProvider) || '',
    weakSkills: [],
    immersion: '',
    dailyCapacity: '',
    scheduleType: '', // NEW: 'commuter', 'balanced', or 'intensive'
    months: 6,
    studyDays: 4, // Default value for backward compatibility
    grammarFocus: 'balanced', // Default value
  });

  // Load subscription status
  useEffect(() => {
    const loadSubscriptionStatus = async () => {
      if (auth.currentUser) {
        try {
          const status = await subscriptionService.getSubscriptionStatus(auth.currentUser.uid);
          setSubscriptionStatus(status);
        } catch (error) {
          console.error('Error loading subscription status:', error);
          setSubscriptionStatus(subscriptionService.getDefaultFreeStatus());
        }
      } else {
        setSubscriptionStatus(subscriptionService.getDefaultFreeStatus());
      }
    };
    loadSubscriptionStatus();
  }, []);

  // Calculate total steps dynamically (4 base steps + 1 conditional Exam Matchmaker if targetExam is empty)
  const getTotalSteps = () => {
    const baseSteps = 4; // Weak skills, Immersion, Daily Reality, Months
    const needsExamMatchmaker = !formData.targetExam || formData.targetExam === '';
    return baseSteps + (needsExamMatchmaker ? 1 : 0);
  };

  const getFinalStep = () => {
    return getTotalSteps();
  };

  const handleNext = async () => {
    const finalStep = getFinalStep();
    if (step < finalStep) {
      setStep(step + 1);
    } else {
      // Final step - check premium before generating plan
      setIsCheckingPremium(true);
      
      let status = subscriptionStatus;
      if (!status && auth.currentUser) {
        try {
          status = await subscriptionService.getSubscriptionStatus(auth.currentUser.uid);
          setSubscriptionStatus(status);
        } catch (error) {
          console.error('Error checking subscription:', error);
          status = subscriptionService.getDefaultFreeStatus();
        }
      } else if (!status) {
        status = subscriptionService.getDefaultFreeStatus();
      }

      setIsCheckingPremium(false);

      const isAdminUser = adminService.isAdmin();
      if (!isAdminUser && (status?.isFree || !status?.isActive)) {
        setStep(5); // Step 5 = premium required message
      } else {
        try {
          setIsCheckingPremium(true);
          // Prepare payload for studyPlanService
          const plan = await studyPlanService.generatePersonalizedPlan({
            currentLevel: formData.currentLevel,
            targetLevel: formData.targetLevel,
            targetExam: formData.targetExam,
            weakSkills: formData.weakSkills,
            immersion: formData.immersion,
            dailyCapacity: formData.dailyCapacity,
            scheduleType: formData.scheduleType, // NEW: Pass scheduleType
            months: formData.months,
            studyDays: formData.studyDays || 4, // Default to 4 if not set
            grammarFocus: formData.grammarFocus || 'balanced',
          });
          setIsCheckingPremium(false);
          onPlanGenerated(plan);
        } catch (error) {
          console.error('Error generating study plan:', error);
          setIsCheckingPremium(false);
          onPlanGenerated(formData);
        }
      }
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  // Map step number to actual step type (accounting for conditional Exam Matchmaker step)
  const getStepType = (stepNum) => {
    const needsExamMatchmaker = !formData.targetExam || formData.targetExam === '';
    if (stepNum === 1) return 'weakSkills';
    if (stepNum === 2) return 'immersion';
    if (stepNum === 3) return 'dailyReality';
    if (stepNum === 4 && needsExamMatchmaker) return 'examMatchmaker';
    if ((stepNum === 4 && !needsExamMatchmaker) || (stepNum === 5 && needsExamMatchmaker)) return 'months';
    return 'unknown';
  };

  const isStepComplete = () => {
    const stepType = getStepType(step);
    switch (stepType) {
      case 'weakSkills': return formData.weakSkills.length > 0;
      case 'immersion': return !!formData.immersion;
      case 'dailyReality': return !!formData.scheduleType && !!formData.dailyCapacity;
      case 'examMatchmaker': return !!formData.targetExam;
      case 'months': return !!formData.months;
      default: return false;
    }
  };

  const toggleWeakSkill = (skill) => {
    setFormData((prev) => {
      const alreadySelected = prev.weakSkills.includes(skill);
      const updated = alreadySelected
        ? prev.weakSkills.filter((item) => item !== skill)
        : [...prev.weakSkills, skill];
      return { ...prev, weakSkills: updated };
    });
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <StepContainer stepKey="step1">
            <div className="bg-white rounded-3xl border-[3px] border-black p-5 sm:p-8 lg:p-12">
              <StepHeader
                icon={Crosshair}
                headline={t('wizard.weakSkills.title')}
                subtext={t('wizard.weakSkills.subtitle')}
              />
              
              <div className="space-y-3 sm:space-y-4">
                <ToggleCard
                  label={t('wizard.weakSkills.reading')}
                  sublabel={t('wizard.weakSkills.readingDesc')}
                  isSelected={formData.weakSkills.includes('reading')}
                  onClick={() => toggleWeakSkill('reading')}
                />
                <ToggleCard
                  label={t('wizard.weakSkills.listening')}
                  sublabel={t('wizard.weakSkills.listeningDesc')}
                  isSelected={formData.weakSkills.includes('listening')}
                  onClick={() => toggleWeakSkill('listening')}
                />
                <ToggleCard
                  label={t('wizard.weakSkills.writing')}
                  sublabel={t('wizard.weakSkills.writingDesc')}
                  isSelected={formData.weakSkills.includes('writing')}
                  onClick={() => toggleWeakSkill('writing')}
                />
                <ToggleCard
                  label={t('wizard.weakSkills.speaking')}
                  sublabel={t('wizard.weakSkills.speakingDesc')}
                  isSelected={formData.weakSkills.includes('speaking')}
                  onClick={() => toggleWeakSkill('speaking')}
                />
                <ToggleCard
                  label={t('wizard.weakSkills.grammar')}
                  sublabel={t('wizard.weakSkills.grammarDesc')}
                  isSelected={formData.weakSkills.includes('grammar')}
                  onClick={() => toggleWeakSkill('grammar')}
                />
                <ToggleCard
                  label={t('wizard.weakSkills.vocabulary')}
                  sublabel={t('wizard.weakSkills.vocabularyDesc')}
                  isSelected={formData.weakSkills.includes('vocabulary')}
                  onClick={() => toggleWeakSkill('vocabulary')}
                />
              </div>
            </div>
          </StepContainer>
        );

      case 2:
        return (
          <StepContainer stepKey="step4">
            <div className="bg-white rounded-3xl border-[3px] border-black p-5 sm:p-8 lg:p-12">
              <StepHeader
                icon={Globe}
                headline={t('wizard.immersion.title')}
                subtext={t('wizard.immersion.subtitle')}
              />
              
              <div className="space-y-3 sm:space-y-4">
                <ToggleCard
                  icon={Globe}
                  label={t('wizard.immersion.rare')}
                  sublabel={t('wizard.immersion.rareDesc')}
                  isSelected={formData.immersion === 'rare'}
                  onClick={() => setFormData({ ...formData, immersion: 'rare' })}
                />
                <ToggleCard
                  icon={Globe}
                  label={t('wizard.immersion.frequent')}
                  sublabel={t('wizard.immersion.frequentDesc')}
                  isSelected={formData.immersion === 'frequent'}
                  onClick={() => setFormData({ ...formData, immersion: 'frequent' })}
                />
              </div>
            </div>
          </StepContainer>
        );

      case 3:
        return (
          <StepContainer stepKey="step3">
            <div className="bg-white rounded-3xl border-[3px] border-black p-5 sm:p-8 lg:p-12">
              <StepHeader
                icon={Clock}
                headline={t('wizard.schedule.title')}
                subtext={t('wizard.schedule.subtitle')}
              />
              
              <div className="space-y-3 sm:space-y-4">
                <ToggleCard
                  icon={Clock}
                  label={t('wizard.schedule.commuter')}
                  sublabel={t('wizard.schedule.commuterDesc')}
                  isSelected={formData.scheduleType === 'commuter'}
                  onClick={() => setFormData({ 
                    ...formData, 
                    scheduleType: 'commuter',
                    dailyCapacity: '45-60'
                  })}
                />
                <ToggleCard
                  icon={Clock}
                  label={t('wizard.schedule.balanced')}
                  sublabel={t('wizard.schedule.balancedDesc')}
                  isSelected={formData.scheduleType === 'balanced'}
                  onClick={() => setFormData({ 
                    ...formData, 
                    scheduleType: 'balanced',
                    dailyCapacity: '90-120'
                  })}
                />
                <ToggleCard
                  icon={Clock}
                  label={t('wizard.schedule.intensive')}
                  sublabel={t('wizard.schedule.intensiveDesc')}
                  isSelected={formData.scheduleType === 'intensive'}
                  onClick={() => setFormData({ 
                    ...formData, 
                    scheduleType: 'intensive',
                    dailyCapacity: '180+'
                  })}
                />
              </div>
            </div>
          </StepContainer>
        );

      case 4: {
        // Conditional: Exam Matchmaker step (only if targetExam is empty)
        const needsExamMatchmaker = !formData.targetExam || formData.targetExam === '';
        if (needsExamMatchmaker) {
          return (
            <StepContainer stepKey="step4-exam-matchmaker">
              <div className="bg-white rounded-3xl border-[3px] border-black p-5 sm:p-8 lg:p-12">
                <StepHeader
                  icon={Target}
                  headline={t('wizard.examMatchmaker.title')}
                  subtext={t('wizard.examMatchmaker.subtitle')}
                />
                
                <div className="space-y-3 sm:space-y-4">
                  <ToggleCard
                    icon={Target}
                    label={t('wizard.examMatchmaker.creative')}
                    sublabel={t('wizard.examMatchmaker.creativeDesc')}
                    isSelected={formData.targetExam === 'Goethe'}
                    onClick={() => setFormData({ ...formData, targetExam: 'Goethe' })}
                  />
                  <ToggleCard
                    icon={Target}
                    label={t('wizard.examMatchmaker.templates')}
                    sublabel={t('wizard.examMatchmaker.templatesDesc')}
                    isSelected={formData.targetExam === 'TELC'}
                    onClick={() => setFormData({ ...formData, targetExam: 'TELC' })}
                  />
                </div>
              </div>
            </StepContainer>
          );
        }
        // If Exam Matchmaker not needed, show months step
        return (
          <StepContainer stepKey="step-months">
            <div className="bg-white rounded-3xl border-[3px] border-black p-5 sm:p-8 lg:p-12">
              <StepHeader
                icon={Calendar}
                headline="When is exam day?"
                subtext="We use this timeline to calculate your exact 'Content Velocity' and schedule your mandatory Mock Exams."
              />
              
              <div className="space-y-6">
                <div>
                  <label htmlFor="months" className="block font-bold text-black text-lg sm:text-xl mb-4">
                    Study Duration (Months)
                  </label>
                  <div className="space-y-4">
                    <input
                      type="range"
                      id="months"
                      min="1"
                      max="6"
                      value={formData.months}
                      onChange={(e) => setFormData({ ...formData, months: parseInt(e.target.value) })}
                      className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-black py-2"
                      style={{
                        background: `linear-gradient(to right, #000 0%, #000 ${((formData.months - 1) / 5) * 100}%, #e5e7eb ${((formData.months - 1) / 5) * 100}%, #e5e7eb 100%)`
                      }}
                    />
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-black/60">1 month</span>
                      <div className="text-center">
                        <div className="text-3xl sm:text-4xl font-black text-black">{formData.months}</div>
                        <div className="text-sm text-black/60">months</div>
                      </div>
                      <span className="text-sm text-black/60">6 months</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </StepContainer>
        );
      }
      case 5:
        // Months step (only reached if Exam Matchmaker was shown at step 4)
        return (
          <StepContainer stepKey="step-months">
            <div className="bg-white rounded-3xl border-[3px] border-black p-5 sm:p-8 lg:p-12">
              <StepHeader
                icon={Calendar}
                headline={t('wizard.months.title')}
                subtext={t('wizard.months.subtitle')}
              />
              
              <div className="space-y-6">
                <div>
                  <label htmlFor="months" className="block font-bold text-black text-lg sm:text-xl mb-4">
                    {t('wizard.months.label')}
                  </label>
                  <div className="space-y-4">
                    <input
                      type="range"
                      id="months"
                      min="1"
                      max="6"
                      value={formData.months}
                      onChange={(e) => setFormData({ ...formData, months: parseInt(e.target.value) })}
                      className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-black py-2"
                      style={{
                        background: `linear-gradient(to right, #000 0%, #000 ${((formData.months - 1) / 5) * 100}%, #e5e7eb ${((formData.months - 1) / 5) * 100}%, #e5e7eb 100%)`
                      }}
                    />
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-black/60">{t('wizard.months.oneMonth')}</span>
                      <div className="text-center">
                        <div className="text-3xl sm:text-4xl font-black text-black">{formData.months}</div>
                        <div className="text-sm text-black/60">{formData.months === 1 ? t('wizard.months.month') : t('wizard.months.months')}</div>
                      </div>
                      <span className="text-sm text-black/60">{t('wizard.months.sixMonths')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </StepContainer>
        );

      case 6:
        // Premium required step
        return (
          <StepContainer stepKey="step5">
            <div className="bg-white rounded-3xl border-[3px] border-black p-5 sm:p-8 lg:p-12">
              <div className="text-center space-y-6">
                <div className="w-20 h-20 bg-black rounded-full flex items-center justify-center mx-auto">
                  <Lock className="w-10 h-10 text-white" />
                </div>
                <div className="space-y-2">
                  <h2 className="text-3xl font-black text-black">
                    {t('premium.required.title')}
                  </h2>
                  <p className="text-lg text-black/60 max-w-md mx-auto">
                    {t('premium.required.description')}
                  </p>
                </div>
                <div className="space-y-3 pt-4">
                  <motion.button
                    onClick={() => navigate('/pricing')}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-black text-white rounded-xl font-bold hover:bg-black/90 transition-all duration-300 shadow-xl"
                  >
                    <Crown className="w-5 h-5" />
                    {t('premium.required.upgrade')}
                    <ArrowRight className="w-5 h-5" />
                  </motion.button>
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="w-full px-6 py-3 text-black/60 hover:text-black transition-colors duration-300"
                  >
                    {t('premium.required.back')}
                  </button>
                </div>
              </div>
            </div>
          </StepContainer>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {!isEmbedded && (
        <div className="bg-white border-b-[3px] border-black">
          <div className="max-w-7xl mx-auto py-16 px-4 sm:py-24 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-black mb-4">
              {t('wizard.title')}
            </h1>
            <p className="mt-6 max-w-2xl mx-auto text-xl text-black/60">
              {t('wizard.subtitle')}
            </p>
          </div>
        </div>
      )}
      
      <div className="max-w-3xl mx-auto py-6 sm:py-12 px-4 sm:px-6 lg:px-8">
        {/* Progress Indicator */}
        {step <= getFinalStep() && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-bold text-black/60">
                {t('wizard.step', { current: step, total: getTotalSteps() })}
              </span>
              <span className="text-sm font-bold text-black/60">
                {t('wizard.progress', { percent: Math.round((step / getTotalSteps()) * 100) })}
              </span>
            </div>
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${(step / getTotalSteps()) * 100}%` }}
                transition={{ duration: 0.3 }}
                className="h-full bg-black"
              />
            </div>
          </div>
        )}

        {/* Step Content with Animation */}
        <AnimatePresence mode="wait" initial={false}>
          {renderStep()}
        </AnimatePresence>

        {/* Navigation Buttons */}
        {step !== 6 && (
          <div className="mt-8 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-0">
            <motion.button
              onClick={handleBack}
              disabled={step === 1}
              whileHover={step > 1 ? { scale: 1.02 } : {}}
              whileTap={step > 1 ? { scale: 0.98 } : {}}
              className={`w-full sm:w-auto px-6 py-3.5 sm:py-3 rounded-xl font-bold transition-all duration-200 ${
                step > 1
                  ? 'bg-white border-2 border-black text-black hover:bg-black hover:text-white'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              {t('wizard.back')}
            </motion.button>

            <motion.button
              onClick={handleNext}
              disabled={!isStepComplete() || isCheckingPremium}
              whileHover={isStepComplete() && !isCheckingPremium ? { scale: 1.02 } : {}}
              whileTap={isStepComplete() && !isCheckingPremium ? { scale: 0.98 } : {}}
              className={`w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 sm:px-8 sm:py-4 rounded-xl font-bold transition-all duration-200 shadow-xl ${
                isStepComplete() && !isCheckingPremium
                  ? 'bg-black text-white hover:bg-black/90'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isCheckingPremium ? (
                <>
                  <LogamiLogoSpinner size="md" decorative={false} alt="Logami loading" />
                  {t('wizard.checking')}
                </>
              ) : (
                <>
                  {step < getFinalStep() ? t('wizard.next') : t('wizard.createPlan')}
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </motion.button>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudyPlanWizard;
